export type ImageGeometry= {x:number;y:number;displayWidth:number;displayHeight:number};
type Entry={version:1;projectId:number;revision:string;savedAt:number;positions:Record<string,ImageGeometry>};
type StorageLike=Pick<Storage,'getItem'|'setItem'|'removeItem'>;
/** Per-tab unsaved edit intent, never image bytes, URLs or brand/chat content.
 * A full document navigation destroys the in-memory HTTP queue. Matching ACKs
 * remove intent; a subsequent owned image read can safely retry existing tiles.
 */
export function createImageGeometryJournal(storage:()=>StorageLike|undefined,now=Date.now,revision=()=>crypto.randomUUID()){
 const key=(id:number)=>`canvas-unsaved-geometry-v1:${id}`;
 const validId=(id:number)=>Number.isSafeInteger(id)&&id>0;
 const validGeometry=(v:any):v is ImageGeometry=>v&&['x','y','displayWidth','displayHeight'].every(k=>typeof v[k]==='number'&&Number.isFinite(v[k])&&Math.abs(v[k])<=10_000_000)&&v.displayWidth>0&&v.displayHeight>0;
 function read(projectId:number):Entry|undefined{
  if(!validId(projectId))return;
  try{
   const raw=storage()?.getItem(key(projectId));if(!raw||raw.length>512_000)return;
   const entry=JSON.parse(raw);
   if(entry?.version!==1||entry.projectId!==projectId||typeof entry.revision!=='string'||entry.revision.length>100||!Number.isFinite(entry.savedAt)||entry.savedAt>now()+60_000||now()-entry.savedAt>86_400_000||!entry.positions||typeof entry.positions!=='object'||Array.isArray(entry.positions))return;
   const positions=Object.entries(entry.positions);if(positions.length>1000||positions.some(([id,value])=>!id||id.length>300||!validGeometry(value)))return;
   const clean:Record<string,ImageGeometry>=Object.create(null);
   for(const [id,value] of positions){const g=value as ImageGeometry;clean[id]={x:g.x,y:g.y,displayWidth:g.displayWidth,displayHeight:g.displayHeight};}
   return{version:1,projectId,revision:entry.revision,savedAt:entry.savedAt,positions:clean};
  }catch{return;}
 }
 function ack(projectId:number,expected:string){try{if(read(projectId)?.revision===expected)storage()?.removeItem(key(projectId));}catch{}}
 function stage(projectId:number,images:Array<ImageGeometry&{id:string}>,dirty:ReadonlySet<string>):Entry|undefined{
  if(!validId(projectId))return;
  const prior=read(projectId),positions:Record<string,ImageGeometry>=Object.create(null);
  for(const image of images){
   if((dirty.has(image.id)||prior?.positions[image.id])&&validGeometry(image))positions[image.id]={x:image.x,y:image.y,displayWidth:image.displayWidth,displayHeight:image.displayHeight};
  }
  if(!Object.keys(positions).length){if(prior)ack(projectId,prior.revision);return;}
  if(Object.keys(positions).length>1000)return;
  const entry:Entry={version:1,projectId,revision:revision(),savedAt:now(),positions};
  try{const target=storage();if(!target)return;target.setItem(key(projectId),JSON.stringify(entry));return entry;}catch{return;}
 }
 return{read,stage,ack};
}
